import styled from "styled-components"
import { getDisplayCalories, getDisplayMealMacro } from "utils"
import { IMostMeal, INutritionType } from "../types"
import ClockIcon from "components/Icons/ClockIcon"
import React, { useState } from "react"
import { MealType } from "model/food/types"
import { isFutureDate } from "utils/dateTime"
import RoundedSwitch, {
  IRoundedSwitchOption,
} from "components/inputs/RoundedSwitch"

const MostMealsWrapper = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const TopFoodsHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
  line-height: 1.6;
`

export const TopFoodsTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: white;
`

export const PeriodText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const MealWrapper = styled.div`
  --order-width: 10px;

  display: flex;
  align-items: center;
  gap: 12px;
  height: 36px;
  color: rgba(255, 255, 255, 0.8);
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
`

const MealOrder = styled.div`
  width: 10px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
`

const MealName = styled.div<{ $isFutureScheduleMeal: boolean }>`
  font-size: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  flex: 1;
  color: ${(props) =>
    props.$isFutureScheduleMeal
      ? "rgba(255, 255, 255, 0.5)"
      : "rgba(255, 255, 255, 0.8)"};
`
const MealCalories = styled.div<{ $isFutureScheduleMeal: boolean }>`
  min-width: 64px;
  font-size: 14px;
  text-align: right;
  flex-shrink: 0;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 2px;
  justify-content: flex-end;
  opacity: ${(props) => (props.$isFutureScheduleMeal ? 0.8 : 1)};
  color: rgba(255, 255, 255, 0.8);
`

const CalUnit = styled.span`
  color: rgba(255, 255, 255, 0.4);
`

export const NoDataText = styled.div`
  padding: 8px var(--spacing_xxs, 4px) 8px var(--spacing_m, 12px);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
`

export const nutritionOptions: IRoundedSwitchOption[] = [
  { label: "Calories", value: "calories" },
  { label: "Protein", value: "protein" },
  { label: "Fat", value: "fat" },
  { label: "Carbs", value: "carbs" },
]

const getDisplayNutritionParts = (
  meal: MealType,
  nutritionType: INutritionType,
): { value: string; unit: string } => {
  switch (nutritionType) {
    case "calories":
      return { value: String(getDisplayCalories(meal.calorie)), unit: "cal" }

    case "protein":
      return { value: getDisplayMealMacro(meal.protein).toString(), unit: "g" }

    case "carbs":
      return {
        value: getDisplayMealMacro(meal.carbohydrate).toString(),
        unit: "g",
      }

    case "fat":
      return { value: getDisplayMealMacro(meal.fat).toString(), unit: "g" }

    default:
      return { value: "", unit: "" }
  }
}

const MostMealItem = ({
  order,
  meal,
  nutritionType,
}: {
  order: number
  meal: MealType
  nutritionType: INutritionType
}) => {
  const isFutureScheduleMeal =
    !!meal.scheduleId && isFutureDate(meal.createdFor)

  const { value, unit } = getDisplayNutritionParts(meal, nutritionType)

  return (
    <MealWrapper>
      <MealOrder>{order}</MealOrder>
      <MealName $isFutureScheduleMeal={isFutureScheduleMeal}>
        {meal.name}
      </MealName>
      <MealCalories $isFutureScheduleMeal={isFutureScheduleMeal}>
        {isFutureScheduleMeal && <ClockIcon />}
        <span>{value}</span>
        <CalUnit>{unit}</CalUnit>
      </MealCalories>
    </MealWrapper>
  )
}

const MostMeals = ({ mostMeals }: { mostMeals: IMostMeal[] }) => {
  const [activeNutrition, setActiveNutrition] =
    useState<INutritionType>("calories")

  const activeMostMeal = mostMeals.find(
    (m) => m.nutritionType === activeNutrition,
  )

  return (
    <MostMealsWrapper>
      <RoundedSwitch
        fullWidth
        options={nutritionOptions}
        value={activeNutrition}
        onChange={(value) => setActiveNutrition(value as INutritionType)}
      />

      {activeMostMeal &&
        (activeMostMeal.meals.length > 0 ? (
          <CardContainer>
            {activeMostMeal.meals.map((meal, index) => (
              <React.Fragment key={index}>
                <MostMealItem
                  order={index + 1}
                  meal={meal}
                  nutritionType={activeMostMeal.nutritionType}
                />
                {index < activeMostMeal.meals.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </CardContainer>
        ) : (
          <NoDataText>No data yet</NoDataText>
        ))}
    </MostMealsWrapper>
  )
}

export default MostMeals
